import React, { useEffect, useMemo, useRef, useState } from "react"
import type {
  EstablishmentData, MunicipalityLookup, TechnicianData, WizardAnswers, WizardArchetype
} from "../../types"
import { ApiError, postJSON } from "../../lib/api"
import { track } from "../../lib/analytics"
import { DEFAULT_COUNCIL, deriveResidueGroups } from "./constants"
import ProgressBar from "./ProgressBar"
import ArchetypeStep from "./steps/ArchetypeStep"
import EstablishmentStep from "./steps/EstablishmentStep"
import QuestionStep from "./steps/QuestionStep"
import TechnicianStep from "./steps/TechnicianStep"
import ReviewStep from "./steps/ReviewStep"
import DocumentPreview from "./DocumentPreview"

interface Props {
  archetypes: WizardArchetype[]
  preselected: string | null
}

type Phase = "archetype" | "establishment" | "questions" | "technician" | "review"

const EMPTY_ESTABLISHMENT: EstablishmentData = {
  fantasy_name: "", cnpj: "", cep: "", logradouro: "", municipio: "", uf: "", porte: ""
}

export default function Wizard({ archetypes, preselected }: Props) {
  const initialArchetype = archetypes.find(a => a.key === preselected) || null
  const [archetype, setArchetype] = useState<WizardArchetype | null>(initialArchetype)
  const [phase, setPhase] = useState<Phase>(initialArchetype ? "establishment" : "archetype")
  const [questionIndex, setQuestionIndex] = useState(0)
  const [establishment, setEstablishment] = useState<EstablishmentData>(EMPTY_ESTABLISHMENT)
  const [technician, setTechnician] = useState<TechnicianData>({
    name: "",
    // Arquétipo pré-selecionado (via URL /gerar/:key) não passa por selectArchetype,
    // então o conselho default é semeado já aqui.
    council: initialArchetype ? (DEFAULT_COUNCIL[initialArchetype.key] || "") : "",
    council_number: "",
    uf: ""
  })
  const [answers, setAnswers] = useState<WizardAnswers>({})
  const [municipality, setMunicipality] = useState<MunicipalityLookup | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitErrors, setSubmitErrors] = useState<string[]>([])
  const [sheetOpen, setSheetOpen] = useState(false)

  const questions = archetype?.questions || []
  const groups = useMemo(() => deriveResidueGroups(archetype, answers), [archetype, answers])

  // Progresso: arquétipo + estabelecimento + N perguntas + RT + revisão
  const totalSteps = 3 + questions.length + 1
  const currentStep = (() => {
    switch (phase) {
      case "archetype": return 0
      case "establishment": return 1
      case "questions": return 2 + questionIndex
      case "technician": return 2 + questions.length
      case "review": return 3 + questions.length
    }
  })()

  // Funil: `wizard_start` uma vez por sessão de wizard, e `wizard_step` só
  // quando o usuário AVANÇA — voltar não reconta o passo, senão a análise de
  // abandono fica inflada.
  const furthestStep = useRef(-1)

  useEffect(() => {
    track("wizard_start", {
      archetype: initialArchetype?.key ?? null,
      preselected: Boolean(preselected)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentStep <= furthestStep.current) return
    furthestStep.current = currentStep

    track("wizard_step", {
      step: phase,
      step_index: currentStep,
      total_steps: totalSteps,
      archetype: archetype?.key ?? null,
      question_index: phase === "questions" ? questionIndex : undefined
    })
  }, [phase, questionIndex, currentStep, totalSteps, archetype])

  function selectArchetype(selected: WizardArchetype) {
    setArchetype(selected)
    setAnswers({})
    setQuestionIndex(0)
    setTechnician(t => ({ ...t, council: DEFAULT_COUNCIL[selected.key] || "" }))
    setPhase("establishment")
  }

  function answerQuestion(key: string, value: boolean) {
    setAnswers(prev => ({ ...prev, [key]: value }))
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1)
    } else {
      setPhase("technician")
    }
  }

  function goBack() {
    setSubmitErrors([])
    switch (phase) {
      case "establishment":
        setPhase("archetype")
        break
      case "questions":
        if (questionIndex > 0) setQuestionIndex(questionIndex - 1)
        else setPhase("establishment")
        break
      case "technician":
        setPhase("questions")
        setQuestionIndex(Math.max(questions.length - 1, 0))
        break
      case "review":
        setPhase("technician")
        break
    }
  }

  async function submit() {
    if (!archetype || submitting) return
    setSubmitting(true)
    setSubmitErrors([])
    try {
      const response = await postJSON<{ preview_url: string }>("/api/pgrss_documents", {
        archetype_key: archetype.key,
        establishment,
        responsible_technician: technician,
        answers
      })
      track("document_created", {
        archetype: archetype.key,
        residue_groups: groups.length,
        uf: establishment.uf,
        requires_engineer_art: Boolean(municipality?.requires_engineer_art)
      })
      window.location.assign(response.preview_url)
    } catch (error) {
      const errors = error instanceof ApiError ? error.errors : ["Algo deu errado. Tente novamente."]
      track("wizard_submit_error", { archetype: archetype.key, reason: errors[0] })
      setSubmitErrors(errors)
      setSubmitting(false)
    }
  }

  const preview = (
    <DocumentPreview
      archetype={archetype}
      establishment={establishment}
      technician={technician}
      groups={groups}
      answeredCount={Object.keys(answers).length}
      municipality={municipality}
    />
  )

  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <a href="/" className="text-lg font-bold text-brand-800">GerarPGRSS</a>
        <span className="text-xs font-medium text-brand-800 bg-brand-100 rounded-full px-3 py-1">
          RDC ANVISA 222/2018
        </span>
      </header>

      <ProgressBar current={currentStep} total={totalSteps} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-28 lg:pb-16 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:gap-12">
        <main className="pt-8 lg:pt-12" key={`${phase}-${questionIndex}`}>
          <div className="animate-rise">
            {phase === "archetype" && (
              <ArchetypeStep archetypes={archetypes} onSelect={selectArchetype} />
            )}
            {phase === "establishment" && archetype && (
              <EstablishmentStep
                data={establishment}
                onChange={setEstablishment}
                onMunicipalityLookup={setMunicipality}
                onBack={goBack}
                onContinue={() => { setPhase("questions"); setQuestionIndex(0) }}
              />
            )}
            {phase === "questions" && questions[questionIndex] && (
              <QuestionStep
                question={questions[questionIndex]}
                index={questionIndex}
                total={questions.length}
                value={answers[questions[questionIndex].key]}
                onAnswer={answerQuestion}
                onBack={goBack}
              />
            )}
            {phase === "technician" && archetype && (
              <TechnicianStep
                archetypeKey={archetype.key}
                data={technician}
                onChange={setTechnician}
                onBack={goBack}
                onContinue={() => setPhase("review")}
              />
            )}
            {phase === "review" && archetype && (
              <ReviewStep
                archetype={archetype}
                establishment={establishment}
                technician={technician}
                groups={groups}
                municipality={municipality}
                submitting={submitting}
                errors={submitErrors}
                onBack={goBack}
                onSubmit={submit}
              />
            )}
          </div>
        </main>

        {/* Preview ao vivo — coluna fixa no desktop */}
        <aside className="hidden lg:block pt-12">
          <div className="sticky top-8">{preview}</div>
        </aside>
      </div>

      {/* Mobile: barra fixa + bottom-sheet */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="fixed bottom-0 inset-x-0 z-40 bg-brand-800 text-white px-5 py-4 flex items-center justify-between shadow-[0_-8px_24px_rgba(4,47,46,0.25)]"
        >
          <span className="text-sm font-medium">
            Seu plano: {groups.length} {groups.length === 1 ? "grupo identificado" : "grupos identificados"}
          </span>
          <span className="text-xs uppercase tracking-wide bg-white/15 rounded-full px-3 py-1">Ver plano</span>
        </button>

        {sheetOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setSheetOpen(false)} />
            <div className="absolute bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-brand-50 p-4 pt-3 animate-sheet-up">
              <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-brand-200" />
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="absolute top-3 right-4 text-sm text-brand-700 font-medium"
              >
                Fechar
              </button>
              {preview}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
